import type { LucideIcon } from 'lucide-react'

/**
 * Los doce operadores del motor de consultas de v2.
 *
 * Es un ARREGLO y no sólo una unión —convención del repo: const + union type— porque tiene que
 * poder compararse en tiempo de ejecución contra el contrato publicado. La lista vive en el
 * servidor (`share/query/query.types.ts`) y esto es un espejo escrito a mano: si se desincroniza
 * **no da error**, porque un operador que v2 no conoce **se descarta en silencio**
 * (`if (!FILTER_OPERATORS.includes(op)) continue`) y el filtro simplemente no se aplica — la
 * pantalla muestra el listado entero como si estuviera filtrado.
 *
 * El espejo lo vigila `negotiations-admin-filter.contract.spec.ts` contra el contrato PUBLICADO,
 * que es el mismo mecanismo con el que se vigilan los campos filtrables y los ordenables.
 */
export const QUERY_FILTER_OPERATORS = [
  '$eq',
  '$ne',
  '$in',
  '$nin',
  '$lt',
  '$lte',
  '$gt',
  '$gte',
  '$contains',
  '$startsWith',
  '$endsWith',
  '$null'
] as const

export type QueryFilterOperator = (typeof QUERY_FILTER_OPERATORS)[number]

export type QueryFilterValue = string | string[]
export type QueryFilterMode = 'single' | 'multi' | 'text'
export type QueryFilterInputType = 'text' | 'number' | 'date'

export type QueryFilterOption = {
  value: string
  label: string
}

export type QueryFilterOperatorOption = {
  value: QueryFilterOperator
  label: string
}

export type QueryFilterGroup = {
  key: string
  label: string
}

export type QueryFilterDefinition<Field extends string> = {
  key: Field
  label: string
  mode: QueryFilterMode
  inputType: QueryFilterInputType
  group: string
  icon: LucideIcon
  options?: QueryFilterOption[]
  operators: QueryFilterOperatorOption[]
  defaultOperator: QueryFilterOperator
}

export type QueryFilterRegistry<Field extends string> = {
  groups: QueryFilterGroup[]
  fields: QueryFilterDefinition<Field>[]
  search: {
    ariaLabel: string
    placeholder: string
  }
}

export type QueryFilterValues<Field extends string> = Record<Field, QueryFilterValue>
export type QueryFilterOperatorMap<Field extends string> = Partial<
  Record<Field, QueryFilterOperator>
>

export const QUERY_FILTER_OPERATOR_OPTIONS = {
  equality: [
    { value: '$eq', label: 'es' },
    { value: '$ne', label: 'no es' }
  ],
  multi: [
    { value: '$in', label: 'incluye' },
    { value: '$nin', label: 'no incluye' }
  ],
  text: [
    { value: '$contains', label: 'contiene' },
    { value: '$eq', label: 'es exactamente' },
    { value: '$ne', label: 'no es' },
    { value: '$startsWith', label: 'empieza con' },
    { value: '$endsWith', label: 'termina con' }
  ],
  number: [
    { value: '$eq', label: 'es' },
    { value: '$ne', label: 'no es' },
    { value: '$gt', label: 'es mayor que' },
    { value: '$gte', label: 'es mayor o igual que' },
    { value: '$lt', label: 'es menor que' },
    { value: '$lte', label: 'es menor o igual que' }
  ],
  date: [
    { value: '$gte', label: 'desde' },
    { value: '$lte', label: 'hasta' },
    { value: '$gt', label: 'después de' },
    { value: '$lt', label: 'antes de' }
  ]
} satisfies Record<string, QueryFilterOperatorOption[]>

/**
 * Punto único de definición para filtros de listados. Valida que el catálogo no
 * tenga keys repetidas ni campos asignados a grupos inexistentes.
 */
export function defineQueryFilterRegistry<Field extends string>(
  registry: QueryFilterRegistry<Field>
): QueryFilterRegistry<Field> {
  if (import.meta.env.DEV) {
    const groupKeys = new Set(registry.groups.map((group) => group.key))
    const fieldKeys = new Set<Field>()

    for (const field of registry.fields) {
      if (fieldKeys.has(field.key)) {
        throw new Error(`QueryFilterRegistry: campo duplicado "${field.key}"`)
      }
      if (!groupKeys.has(field.group)) {
        throw new Error(
          `QueryFilterRegistry: el campo "${field.key}" usa el grupo inexistente "${field.group}"`
        )
      }
      if (!field.operators.some((operator) => operator.value === field.defaultOperator)) {
        throw new Error(
          `QueryFilterRegistry: el operador predeterminado de "${field.key}" no está permitido`
        )
      }
      fieldKeys.add(field.key)
    }
  }

  return registry
}

export function getQueryFilterDefaultOperators<Field extends string>(
  registry: QueryFilterRegistry<Field>
): Record<Field, QueryFilterOperator> {
  return Object.fromEntries(
    registry.fields.map((field) => [field.key, field.defaultOperator])
  ) as Record<Field, QueryFilterOperator>
}
